import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Clusterer, Placemark } from "@pbe/react-yandex-maps";
import {
  FaArrowLeft,
  FaChevronDown,
  FaDownload,
  FaEdit,
  FaEye,
  FaFile,
  FaFolder,
  FaFolderPlus,
  FaHome,
  FaMapMarkerAlt,
  FaPlus,
  FaSave,
  FaTrash,
  FaUpload,
} from "react-icons/fa";

import { URL } from "../../utils/constants";
import "./ArchiveBuildings.css";


const emptyBuilding = {
  address: "",
  lat: "",
  long: "",
  line: "",
  object_ref: "",
  documentation_folder: "",
  is_visible: true,
};


const authHeaders = (extra = {}) => {
  const token = localStorage.getItem("auth_token");
  return {
    ...(token ? { Authorization: `Token ${token}` } : {}),
    ...extra,
  };
};


const asList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};


const readError = async (response) => {
  const data = await response.json().catch(() => null);
  if (typeof data?.detail === "string") return data.detail;
  if (data && typeof data === "object") {
    return Object.entries(data)
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
      .join("; ");
  }
  return `HTTP ${response.status}`;
};


const apiRequest = async (path, options = {}) => {
  const isForm = options.body instanceof FormData;
  const response = await fetch(`${URL}${path}`, {
    ...options,
    headers: authHeaders({
      ...(!isForm && options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    }),
  });

  if (!response.ok) throw new Error(await readError(response));
  if (response.status === 204) return null;
  return response.json();
};


const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");


const formatCoordinate = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(6) : "-";
};


export function ArchiveBuildingsLayer({ buildings = [], visible = true }) {
  const points = useMemo(
    () =>
      buildings.filter(
        (item) =>
          item?.is_visible !== false &&
          Number.isFinite(Number(item?.lat)) &&
          Number.isFinite(Number(item?.long))
      ),
    [buildings]
  );

  if (!visible || points.length === 0) return null;

  return (
    <Clusterer
      options={{
        preset: "islands#grayClusterIcons",
        gridSize: 60,
        clusterDisableClickZoom: false,
        clusterOpenBalloonOnClick: false,
      }}
    >
      {points.map((building) => {
        const source = building.source;
        const documentation = building.documentation_url
          ? `<a href="${escapeHtml(building.documentation_url)}">Открыть документацию</a>`
          : "-";
        const sourceBlock = source
          ? `
              <div class="archive-marker-source">
                <div><b>Наименование архива:</b> ${escapeHtml(source.archive_name || "Внешний диск")}</div>
                <div><b>Адрес на внешнем диске:</b> ${escapeHtml(source.source_path || "-")}</div>
              </div>
            `
          : "";

        return (
          <Placemark
            key={building.id}
            geometry={[Number(building.lat), Number(building.long)]}
            properties={{
              balloonContentHeader: `<a class="archive-building-balloon-link" href="/archive-buildings/${building.id}">${escapeHtml(building.address || "Архивное строение")}</a>`,
              balloonContentBody: `
                <div><b>Адрес:</b> ${escapeHtml(building.address || "-")}</div>
                <div><b>Координаты:</b> ${formatCoordinate(building.lat)}, ${formatCoordinate(building.long)}</div>
                <div><b>Линия:</b> ${escapeHtml(building.line_name || "-")}</div>
                <div><b>Объект:</b> ${escapeHtml(building.object_name || "-")}</div>
                <div><b>Документация:</b> ${documentation}</div>
                <div><b>Область видимости:</b> Архив</div>
                ${sourceBlock}
              `,
              hintContent: building.address || "Архивное строение",
            }}
            options={{
              preset: "islands#grayHomeIcon",
              draggable: false,
              zIndex: 24,
            }}
          />
        );
      })}
    </Clusterer>
  );
}


export function ArchiveBuildingsPanel({
  buildings = [],
  mapRef,
  ymapsApiRef,
  canManage = false,
  onChanged,
}) {
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(emptyBuilding);
  const [lines, setLines] = useState([]);
  const [objects, setObjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [discovered, setDiscovered] = useState([]);
  const [selectedDiscovered, setSelectedDiscovered] = useState("");
  const [folderDraft, setFolderDraft] = useState({
    name: "",
    relative_path: "",
    source_name: "Внешний диск",
    source_path: "",
  });
  const [picking, setPicking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressOpen, setAddressOpen] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressGeocoding, setAddressGeocoding] = useState(false);
  const [addressHighlighted, setAddressHighlighted] = useState(-1);
  const addressRequestIdRef = useRef(0);
  const skipNextAddressSuggestRef = useRef(false);
  const previewPlacemarkRef = useRef(null);

  const getYmapsApi = useCallback(
    () => ymapsApiRef?.current || window.ymaps,
    [ymapsApiRef]
  );

  const removePreviewPlacemark = useCallback(() => {
    const map = mapRef?.current;
    const placemark = previewPlacemarkRef.current;
    if (map && placemark) {
      try {
        map.geoObjects.remove(placemark);
      } catch {}
    }
    previewPlacemarkRef.current = null;
  }, [mapRef]);

  const placePreviewPlacemark = useCallback((coordinates, address = "") => {
    const map = mapRef?.current;
    const api = getYmapsApi();
    if (!map?.geoObjects || !api?.Placemark || !Array.isArray(coordinates)) return;

    let placemark = previewPlacemarkRef.current;
    if (!placemark) {
      placemark = new api.Placemark(
        coordinates,
        {
          hintContent: "Предпросмотр архивной точки",
          balloonContentHeader: "Временная архивная точка",
          balloonContentBody: escapeHtml(address || "Адрес ещё не указан"),
        },
        {
          preset: "islands#grayHomeIcon",
          draggable: true,
          zIndex: 5000,
          zIndexHover: 5001,
        }
      );
      placemark.events.add("dragend", () => {
        const nextCoordinates = placemark.geometry.getCoordinates();
        setForm((current) => ({
          ...current,
          lat: Number(nextCoordinates[0]).toFixed(7),
          long: Number(nextCoordinates[1]).toFixed(7),
        }));
      });
      map.geoObjects.add(placemark);
      previewPlacemarkRef.current = placemark;
      return;
    }

    placemark.geometry.setCoordinates(coordinates);
    placemark.properties.set("balloonContentBody", escapeHtml(address || "Адрес ещё не указан"));
  }, [getYmapsApi, mapRef]);

  const suggestAddress = useCallback(async (text) => {
    const api = getYmapsApi();
    const query = String(text || "").trim();
    if (!api || query.length < 2) return [];

    if (typeof api.suggest === "function") {
      try {
        const items = await api.suggest(query, { results: 7 });
        const suggestions = (items || [])
          .map((item) => item?.displayName || item?.value || "")
          .filter(Boolean);
        if (suggestions.length) return suggestions;
      } catch {}
    }

    try {
      const result = await api.geocode(query, { results: 7 });
      const geoObjects = result?.geoObjects;
      if (!geoObjects) return [];

      const suggestions = [];
      for (let index = 0; index < geoObjects.getLength(); index += 1) {
        const item = geoObjects.get(index);
        const label =
          item?.getAddressLine?.() ||
          item?.properties?.get?.("text") ||
          item?.properties?.get?.("name") ||
          "";
        if (label) suggestions.push(label);
      }
      return suggestions;
    } catch {
      return [];
    }
  }, [getYmapsApi]);

  const applyAddress = useCallback(async (value) => {
    const query = String(value || "").trim();
    if (!query) return;

    addressRequestIdRef.current += 1;
    setAddressOpen(false);
    setAddressLoading(false);
    setAddressHighlighted(-1);
    setAddressGeocoding(true);
    setError("");

    try {
      const api = getYmapsApi();
      if (!api?.geocode) throw new Error("Сервис поиска адресов ещё не загружен.");

      const result = await api.geocode(query, { results: 1 });
      const first = result?.geoObjects?.get?.(0);
      const coordinates = first?.geometry?.getCoordinates?.();
      if (!first || !Array.isArray(coordinates) || coordinates.length !== 2) {
        throw new Error("Адрес не найден. Уточните запрос.");
      }

      const address =
        first.getAddressLine?.() ||
        first.properties?.get?.("text") ||
        first.properties?.get?.("name") ||
        query;

      skipNextAddressSuggestRef.current = true;
      setForm((current) => ({
        ...current,
        address,
        lat: Number(coordinates[0]).toFixed(7),
        long: Number(coordinates[1]).toFixed(7),
      }));
      setAddressOptions([]);
      placePreviewPlacemark(coordinates, address);
      mapRef?.current?.setCenter?.(coordinates, 16);
    } catch (requestError) {
      setError(requestError.message || "Не удалось найти адрес.");
    } finally {
      setAddressGeocoding(false);
    }
  }, [getYmapsApi, mapRef, placePreviewPlacemark]);

  const loadReferences = async () => {
    try {
      const [lineData, objectData, folderData, discoveredData] = await Promise.all([
        apiRequest("/api/lines/"),
        apiRequest("/api/ock-objects/"),
        apiRequest("/api/archive-doc-folders/"),
        apiRequest("/api/archive-doc-folders/discover/"),
      ]);
      setLines(asList(lineData));
      setObjects(asList(objectData));
      setFolders(asList(folderData));
      setDiscovered(asList(discoveredData));
    } catch (requestError) {
      setError(requestError.message || "Не удалось загрузить справочники.");
    }
  };

  useEffect(() => {
    loadReferences();
  }, []);

  useEffect(() => removePreviewPlacemark, [removePreviewPlacemark]);

  useEffect(() => {
    if (skipNextAddressSuggestRef.current) {
      skipNextAddressSuggestRef.current = false;
      return undefined;
    }

    const query = form.address.trim();
    if (query.length < 2) {
      setAddressOptions([]);
      setAddressOpen(false);
      setAddressLoading(false);
      setAddressHighlighted(-1);
      return undefined;
    }

    let active = true;
    const requestId = ++addressRequestIdRef.current;
    setAddressLoading(true);

    const timeoutId = window.setTimeout(async () => {
      const suggestions = await suggestAddress(query);
      if (!active || requestId !== addressRequestIdRef.current) return;

      setAddressOptions(suggestions);
      setAddressOpen(true);
      setAddressHighlighted(suggestions.length ? 0 : -1);
      setAddressLoading(false);
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [form.address, suggestAddress]);

  useEffect(() => {
    if (!picking) return undefined;
    const map = mapRef?.current;
    if (!map?.events) return undefined;

    const handlePick = (event) => {
      const coordinates = event.get("coords");
      if (!Array.isArray(coordinates) || coordinates.length !== 2) return;
      setForm((current) => ({
        ...current,
        lat: Number(coordinates[0]).toFixed(7),
        long: Number(coordinates[1]).toFixed(7),
      }));
      placePreviewPlacemark(coordinates, form.address);
      setPicking(false);
    };

    map.events.add("click", handlePick);
    map.events.add("tap", handlePick);
    return () => {
      try {
        map.events.remove("click", handlePick);
        map.events.remove("tap", handlePick);
      } catch {}
    };
  }, [picking, mapRef, placePreviewPlacemark, form.address]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    removePreviewPlacemark();
    setEditingId("");
    setForm(emptyBuilding);
    setPicking(false);
    setAddressOptions([]);
    setAddressOpen(false);
    setAddressHighlighted(-1);
    setError("");
    setMessage("");
  };

  const selectBuilding = (id) => {
    removePreviewPlacemark();
    setEditingId(id);
    setError("");
    setMessage("");
    if (!id) {
      setForm(emptyBuilding);
      setAddressOptions([]);
      setAddressOpen(false);
      return;
    }
    const item = buildings.find((building) => String(building.id) === String(id));
    if (!item) return;
    skipNextAddressSuggestRef.current = true;
    setForm({
      address: item.address || "",
      lat: item.lat ?? "",
      long: item.long ?? "",
      line: item.line ?? "",
      object_ref: item.object_ref ?? "",
      documentation_folder: item.documentation_folder ?? "",
      is_visible: item.is_visible !== false,
    });
  };

  const saveBuilding = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    const lat = Number(form.lat);
    const long = Number(form.long);
    if (!form.address.trim()) {
      setError("Укажите адрес.");
      return;
    }
    if (!Number.isFinite(lat) || !Number.isFinite(long)) {
      setError("Выберите точку на карте или введите корректные координаты.");
      return;
    }

    const payload = {
      address: form.address.trim(),
      lat,
      long,
      line: form.line || null,
      object_ref: form.object_ref || null,
      documentation_folder: form.documentation_folder || null,
      is_visible: Boolean(form.is_visible),
    };

    setLoading(true);
    try {
      const isEditing = Boolean(editingId);
      await apiRequest(
        isEditing
          ? `/api/archive-buildings/${editingId}/`
          : "/api/archive-buildings/",
        {
          method: isEditing ? "PATCH" : "POST",
          body: JSON.stringify(payload),
        }
      );
      setMessage(isEditing ? "Архивное строение обновлено." : "Архивное строение создано.");
      await onChanged?.();
      removePreviewPlacemark();
      if (!isEditing) setForm(emptyBuilding);
    } catch (requestError) {
      setError(requestError.message || "Не удалось сохранить строение.");
    } finally {
      setLoading(false);
    }
  };

  const deleteBuilding = async () => {
    if (!editingId) return;
    if (!window.confirm("Удалить архивное строение? Папка и файлы останутся в хранилище.")) return;
    setLoading(true);
    setError("");
    try {
      await apiRequest(`/api/archive-buildings/${editingId}/`, { method: "DELETE" });
      resetForm();
      await onChanged?.();
    } catch (requestError) {
      setError(requestError.message || "Не удалось удалить строение.");
    } finally {
      setLoading(false);
    }
  };

  const chooseDiscovered = (relativePath) => {
    setSelectedDiscovered(relativePath);
    const item = discovered.find((entry) => entry.relative_path === relativePath);
    if (!item) return;
    setFolderDraft((current) => ({
      ...current,
      name: item.name,
      relative_path: item.relative_path,
    }));
    if (item.folder_id) {
      updateForm("documentation_folder", item.folder_id);
    }
  };

  const registerFolder = async () => {
    setError("");
    setMessage("");
    if (!folderDraft.name.trim() || !folderDraft.relative_path.trim()) {
      setError("Укажите название и путь папки.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: folderDraft.name.trim(),
        relative_path: folderDraft.relative_path.trim(),
      };
      if (canManage) {
        payload.source_name = folderDraft.source_name.trim() || "Внешний диск";
        payload.source_path = folderDraft.source_path.trim();
      }
      const created = await apiRequest("/api/archive-doc-folders/", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      updateForm("documentation_folder", created.id);
      setMessage("Папка зарегистрирована и выбрана для строения.");
      await loadReferences();
    } catch (requestError) {
      setError(requestError.message || "Не удалось зарегистрировать папку.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="archive-buildings-panel">
      <div className="archive-buildings-panel__title">Архивные строения</div>

      <label className="archive-field">
        <span>Режим</span>
        <select value={editingId} onChange={(event) => selectBuilding(event.target.value)}>
          <option value="">Новое строение</option>
          {canManage && buildings.map((building) => (
            <option key={building.id} value={building.id}>
              {building.address || `ID ${building.id}`}
            </option>
          ))}
        </select>
      </label>

      <form onSubmit={saveBuilding}>
        <div className="archive-field archive-address-field">
          <label htmlFor="archive-building-address">Адрес</label>
          <div
            className="archive-address-control"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setAddressOpen(false);
              }
            }}
          >
            <input
              id="archive-building-address"
              value={form.address}
              onChange={(event) => updateForm("address", event.target.value)}
              onFocus={() => {
                if (addressOptions.length || form.address.trim().length >= 2) {
                  setAddressOpen(true);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setAddressOpen(true);
                  setAddressHighlighted((current) => {
                    if (!addressOptions.length) return -1;
                    return current < 0 ? 0 : Math.min(current + 1, addressOptions.length - 1);
                  });
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setAddressHighlighted((current) => (current <= 0 ? 0 : current - 1));
                } else if (event.key === "Enter") {
                  event.preventDefault();
                  const selected = addressOptions[addressHighlighted] || form.address;
                  applyAddress(selected);
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setAddressOpen(false);
                  setAddressHighlighted(-1);
                }
              }}
              placeholder="Начните вводить адрес"
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={addressOpen}
              aria-controls="archive-address-suggestions"
            />

            {(addressOpen || addressLoading || addressGeocoding) && (
              <div
                id="archive-address-suggestions"
                className="archive-address-suggestions"
                role="listbox"
              >
                {(addressLoading || addressGeocoding) && (
                  <div className="archive-address-suggestion archive-address-suggestion--muted">
                    {addressGeocoding ? "Определение координат..." : "Поиск..."}
                  </div>
                )}

                {!addressLoading && !addressGeocoding && addressOptions.length === 0 && (
                  <div className="archive-address-suggestion archive-address-suggestion--muted">
                    Ничего не найдено
                  </div>
                )}

                {!addressLoading && !addressGeocoding && addressOptions.map((option, index) => (
                  <button
                    key={`${option}-${index}`}
                    type="button"
                    className={`archive-address-suggestion ${index === addressHighlighted ? "is-active" : ""}`}
                    role="option"
                    aria-selected={index === addressHighlighted}
                    onMouseEnter={() => setAddressHighlighted(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyAddress(option)}
                    title={option}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          className={`archive-action archive-action--pick ${picking ? "is-active" : ""}`}
          onClick={() => setPicking((current) => !current)}
        >
          <FaMapMarkerAlt />
          {picking ? "Отменить выбор" : "Выбрать точку на карте"}
        </button>

        <div className="archive-coordinate-grid">
          <label className="archive-field">
            <span>Широта</span>
            <input
              type="number"
              step="any"
              value={form.lat}
              onChange={(event) => updateForm("lat", event.target.value)}
            />
          </label>
          <label className="archive-field">
            <span>Долгота</span>
            <input
              type="number"
              step="any"
              value={form.long}
              onChange={(event) => updateForm("long", event.target.value)}
            />
          </label>
        </div>

        <label className="archive-field">
          <span>Линия</span>
          <select value={form.line} onChange={(event) => updateForm("line", event.target.value)}>
            <option value="">-</option>
            {lines.map((line) => (
              <option key={line.id} value={line.id}>{line.name}</option>
            ))}
          </select>
        </label>

        <label className="archive-field">
          <span>Объект проекта</span>
          <select
            value={form.object_ref}
            onChange={(event) => updateForm("object_ref", event.target.value)}
          >
            <option value="">-</option>
            {objects.map((object) => (
              <option key={object.id} value={object.id}>{object.name}</option>
            ))}
          </select>
        </label>

        <label className="archive-field">
          <span>Папка документации</span>
          <select
            value={form.documentation_folder}
            onChange={(event) => updateForm("documentation_folder", event.target.value)}
          >
            <option value="">Не привязана</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}{folder.is_present ? "" : " (нет на диске)"}
              </option>
            ))}
          </select>
        </label>

        <label className="archive-checkbox">
          <input
            type="checkbox"
            checked={form.is_visible}
            onChange={(event) => updateForm("is_visible", event.target.checked)}
          />
          <span>Показывать на карте</span>
        </label>

        <div className="archive-button-row">
          <button type="submit" className="archive-action archive-action--save" disabled={loading}>
            <FaSave />
            {editingId ? "Сохранить" : "Создать"}
          </button>
          {editingId && canManage && (
            <button
              type="button"
              className="archive-action archive-action--danger"
              onClick={deleteBuilding}
              disabled={loading}
              title="Удалить строение"
            >
              <FaTrash />
            </button>
          )}
        </div>
      </form>

      <div className="archive-folder-register">
        <div className="archive-folder-register__title">Регистрация папки</div>
        <label className="archive-field">
          <span>Найденные папки в mapcloud/arch</span>
          <select value={selectedDiscovered} onChange={(event) => chooseDiscovered(event.target.value)}>
            <option value="">Выберите папку</option>
            {discovered.map((item) => (
              <option key={item.relative_path} value={item.relative_path}>
                {item.name}{item.is_registered ? " (зарегистрирована)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="archive-field">
          <span>Название</span>
          <input
            value={folderDraft.name}
            onChange={(event) => setFolderDraft((current) => ({ ...current, name: event.target.value }))}
          />
        </label>
        <label className="archive-field">
          <span>Путь в хранилище</span>
          <input
            value={folderDraft.relative_path}
            onChange={(event) => setFolderDraft((current) => ({ ...current, relative_path: event.target.value }))}
            placeholder="arch/Название папки"
          />
        </label>
        {canManage && (
          <>
            <label className="archive-field">
              <span>Наименование архива</span>
              <input
                value={folderDraft.source_name}
                onChange={(event) => setFolderDraft((current) => ({ ...current, source_name: event.target.value }))}
              />
            </label>
            <label className="archive-field">
              <span>Адрес на внешнем диске</span>
              <input
                value={folderDraft.source_path}
                onChange={(event) => setFolderDraft((current) => ({ ...current, source_path: event.target.value }))}
              />
            </label>
          </>
        )}
        <button
          type="button"
          className="archive-action archive-action--folder"
          onClick={registerFolder}
          disabled={loading}
        >
          <FaPlus />
          Зарегистрировать папку
        </button>
      </div>

      {error && <div className="archive-panel-message archive-panel-message--error">{error}</div>}
      {message && <div className="archive-panel-message archive-panel-message--success">{message}</div>}
    </div>
  );
}


const formatSize = (size) => {
  const value = Number(size);
  if (!Number.isFinite(value)) return "-";
  if (value < 1024) return `${value} Б`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} КБ`;
  if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} МБ`;
  return `${(value / 1024 ** 3).toFixed(1)} ГБ`;
};


export function ArchiveBuildingCard() {
  const { id } = useParams();
  const history = useHistory();
  const documentationInputRef = useRef(null);
  const [building, setBuilding] = useState(null);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [canLinkFolder, setCanLinkFolder] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [folderQuery, setFolderQuery] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const refreshCard = useCallback(async () => {
    const [buildingData, folderData] = await Promise.all([
      apiRequest(`/api/archive-buildings/${id}/`),
      apiRequest(`/api/archive-buildings/${id}/available-folders/`),
    ]);
    setBuilding(buildingData);
    setAvailableFolders(asList(folderData?.items));
    setCanLinkFolder(Boolean(folderData?.can_link));
  }, [id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      apiRequest(`/api/archive-buildings/${id}/`),
      apiRequest(`/api/archive-buildings/${id}/available-folders/`),
    ])
      .then(([buildingData, folderData]) => {
        if (!active) return;
        setBuilding(buildingData);
        setAvailableFolders(asList(folderData?.items));
        setCanLinkFolder(Boolean(folderData?.can_link));
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || "Не удалось открыть карточку.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  const filteredFolders = useMemo(() => {
    const query = folderQuery.trim().toLowerCase();
    return availableFolders.filter((folder) => {
      if (folder.is_attached) return false;
      return !query || String(folder.name || "").toLowerCase().includes(query);
    });
  }, [availableFolders, folderQuery]);

  const attachFolder = async () => {
    if (!selectedFolder) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await apiRequest(`/api/archive-buildings/${id}/attach-folder/`, {
        method: "POST",
        body: JSON.stringify({ folder_name: selectedFolder }),
      });
      await refreshCard();
      setSelectedFolder("");
      setAttachOpen(false);
      setMessage("Папка документации прикреплена.");
    } catch (requestError) {
      setError(requestError.message || "Не удалось прикрепить папку.");
    } finally {
      setBusy(false);
    }
  };

  const detachFolder = async () => {
    if (!building?.documentation_folder) return;
    if (!window.confirm("Открепить папку документации от архивного строения? Файлы останутся в хранилище.")) return;

    setBusy(true);
    setError("");
    setMessage("");
    try {
      await apiRequest(`/api/archive-buildings/${id}/detach-folder/`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await refreshCard();
      setMessage("Папка документации откреплена.");
    } catch (requestError) {
      setError(requestError.message || "Не удалось открепить папку.");
    } finally {
      setBusy(false);
    }
  };

  const uploadDocumentation = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    setBusy(true);
    setError("");
    setMessage("");
    try {
      const body = new FormData();
      files.forEach((file) => body.append("files", file, file.name));
      body.append("path", "");
      await apiRequest(`/api/archive-buildings/${id}/upload/`, {
        method: "POST",
        body,
      });
      await refreshCard();
      setMessage(files.length === 1 ? "Документ прикреплён." : `Прикреплено документов: ${files.length}.`);
    } catch (requestError) {
      setError(requestError.message || "Не удалось прикрепить документацию.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="archive-docs-state">Загрузка карточки...</div>;

  return (
    <div className="archive-card-page">
      <div className="archive-card">
        <div className="archive-card-scroll">
          <div className="archive-card-title-row">
            <button className="archive-card-icon-button" onClick={() => history.push("/")} title="Вернуться на карту">
              <FaArrowLeft />
            </button>
            <h1 className="archive-card-title">{building?.address || "Архивное строение"}</h1>
          </div>

          {error && <div className="archive-card-error">{error}</div>}
          {message && <div className="archive-card-message">{message}</div>}

          {building && (
            <>
              <section className="archive-card-panel">
                <h2>Сведения</h2>
                <dl className="archive-card-details">
                  <div><dt>Адрес</dt><dd>{building.address || "-"}</dd></div>
                  <div><dt>Координаты</dt><dd>{formatCoordinate(building.lat)}, {formatCoordinate(building.long)}</dd></div>
                  <div><dt>Линия</dt><dd>{building.line_name || "-"}</dd></div>
                  <div><dt>Объект</dt><dd>{building.object_name || "-"}</dd></div>
                  <div><dt>Область видимости</dt><dd>Архив</dd></div>
                </dl>
              </section>

              <section className="archive-card-panel">
                <h2>Документация</h2>
                {building.documentation_folder ? (
                  <div className="archive-card-folder-row">
                    <button
                      type="button"
                      className="archive-card-folder-link"
                      onClick={() => history.push(building.documentation_url)}
                      title="Открыть документацию"
                    >
                      <FaFolder />
                      <span>{building.documentation_folder_name || "Документация"}</span>
                    </button>
                    {canLinkFolder && (
                      <button
                        type="button"
                        className="archive-card-icon-button archive-card-icon-button--danger"
                        onClick={detachFolder}
                        disabled={busy}
                        title="Открепить папку"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="archive-card-documentation-empty">
                    <div className="archive-card-empty">Документация не прикреплена</div>
                    {canLinkFolder && (
                      <>
                        <input
                          ref={documentationInputRef}
                          type="file"
                          multiple
                          hidden
                          onChange={uploadDocumentation}
                        />
                        <button
                          type="button"
                          className="archive-card-primary archive-card-upload"
                          onClick={() => documentationInputRef.current?.click()}
                          disabled={busy}
                        >
                          <FaUpload />
                          <span>{busy ? "Загрузка..." : "Загрузить документы"}</span>
                        </button>
                        <div className="archive-card-choice"><span>или</span></div>
                      </>
                    )}
                  </div>
                )}
              </section>

              {canLinkFolder && (
                <section className="archive-card-attach-panel">
                  <button
                    type="button"
                    className="archive-card-collapse"
                    onClick={() => setAttachOpen((current) => !current)}
                    aria-expanded={attachOpen}
                  >
                    <span>Выбрать папку с документацией</span>
                    <FaChevronDown className={attachOpen ? "is-open" : ""} />
                  </button>

                  {attachOpen && (
                    <div className="archive-card-attach-body">
                      <input
                        className="archive-card-search"
                        value={folderQuery}
                        onChange={(event) => setFolderQuery(event.target.value)}
                        placeholder="Поиск по названию папки..."
                      />
                      <div className="archive-card-folder-list" role="listbox">
                        {filteredFolders.length ? filteredFolders.map((folder) => (
                          <button
                            key={folder.name}
                            type="button"
                            role="option"
                            aria-selected={selectedFolder === folder.name}
                            className={selectedFolder === folder.name ? "is-selected" : ""}
                            onClick={() => setSelectedFolder(folder.name)}
                            title={folder.name}
                          >
                            <span>{folder.name}</span>
                            <span aria-hidden="true">{selectedFolder === folder.name ? "✓" : ""}</span>
                          </button>
                        )) : (
                          <div className="archive-card-empty">В папке arch нет доступных папок</div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="archive-card-primary"
                        onClick={attachFolder}
                        disabled={busy || !selectedFolder}
                      >
                        Прикрепить выбранную папку
                      </button>
                    </div>
                  )}
                </section>
              )}

              {building.source && (
                <section className="archive-card-panel">
                  <h2>Источник</h2>
                  <dl className="archive-card-details">
                    <div><dt>Наименование архива</dt><dd>{building.source.archive_name || "Внешний диск"}</dd></div>
                    <div><dt>Адрес на внешнем диске</dt><dd>{building.source.source_path || "-"}</dd></div>
                  </dl>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


export function ArchiveBuildingDocuments() {
  const { id } = useParams();
  const history = useHistory();
  const fileInputRef = useRef(null);
  const [building, setBuilding] = useState(null);
  const [listing, setListing] = useState({
    path: "",
    parent: "",
    entries: [],
    can_manage: false,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadListing = useCallback(async (path = "") => {
    const data = await apiRequest(
      `/api/archive-buildings/${id}/files/?path=${encodeURIComponent(path)}`
    );
    setListing(data);
  }, [id]);

  const loadAll = useCallback(async (path = "") => {
    setLoading(true);
    setError("");
    try {
      const [buildingData] = await Promise.all([
        apiRequest(`/api/archive-buildings/${id}/`),
        loadListing(path),
      ]);
      setBuilding(buildingData);
    } catch (requestError) {
      setError(requestError.message || "Не удалось открыть документацию.");
    } finally {
      setLoading(false);
    }
  }, [id, loadListing]);

  useEffect(() => {
    loadAll("");
  }, [loadAll]);

  const openAuthenticatedFile = async (entry, mode) => {
    setError("");
    const popup = mode === "preview" ? window.open("", "_blank") : null;
    try {
      const response = await fetch(
        `${URL}/api/archive-buildings/${id}/${mode}/?path=${encodeURIComponent(entry.path)}`,
        { headers: authHeaders() }
      );
      if (!response.ok) throw new Error(await readError(response));
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      if (mode === "preview") {
        if (popup) popup.location = blobUrl;
        else window.open(blobUrl, "_blank", "noopener,noreferrer");
      } else {
        const anchor = document.createElement("a");
        anchor.href = blobUrl;
        anchor.download = entry.name;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (requestError) {
      popup?.close();
      setError(requestError.message || "Не удалось открыть файл.");
    }
  };

  const uploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setError("");
    try {
      for (const file of files) {
        const body = new FormData();
        body.append("file", file);
        body.append("path", listing.path || "");
        await apiRequest(`/api/archive-buildings/${id}/upload/`, {
          method: "POST",
          body,
        });
      }
      await loadListing(listing.path);
    } catch (requestError) {
      setError(requestError.message || "Не удалось загрузить файл.");
    } finally {
      setBusy(false);
    }
  };

  const createFolder = async () => {
    const name = window.prompt("Название новой папки:");
    if (!name?.trim()) return;
    setBusy(true);
    setError("");
    try {
      await apiRequest(`/api/archive-buildings/${id}/mkdir/`, {
        method: "POST",
        body: JSON.stringify({ path: listing.path || "", name: name.trim() }),
      });
      await loadListing(listing.path);
    } catch (requestError) {
      setError(requestError.message || "Не удалось создать папку.");
    } finally {
      setBusy(false);
    }
  };

  const renameEntry = async (entry) => {
    const newName = window.prompt("Новое название:", entry.name);
    if (!newName?.trim() || newName.trim() === entry.name) return;
    setBusy(true);
    setError("");
    try {
      await apiRequest(`/api/archive-buildings/${id}/rename-entry/`, {
        method: "POST",
        body: JSON.stringify({ path: entry.path, new_name: newName.trim() }),
      });
      await loadListing(listing.path);
    } catch (requestError) {
      setError(requestError.message || "Не удалось переименовать объект.");
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Удалить «${entry.name}» без возможности восстановления?`)) return;
    setBusy(true);
    setError("");
    try {
      await apiRequest(`/api/archive-buildings/${id}/delete-entry/`, {
        method: "POST",
        body: JSON.stringify({ path: entry.path }),
      });
      await loadListing(listing.path);
    } catch (requestError) {
      setError(requestError.message || "Не удалось удалить объект.");
    } finally {
      setBusy(false);
    }
  };

  const breadcrumbs = useMemo(() => {
    const parts = (listing.path || "").split("/").filter(Boolean);
    return parts.map((name, index) => ({
      name,
      path: parts.slice(0, index + 1).join("/"),
    }));
  }, [listing.path]);

  if (loading) return <div className="archive-docs-state">Загрузка документации...</div>;

  return (
    <div className="archive-docs-page">
      <header className="archive-docs-header">
        <button
          className="archive-icon-button"
          onClick={() => history.push(`/archive-buildings/${id}`)}
          title="Вернуться к карточке"
        >
          <FaArrowLeft />
        </button>
        <div className="archive-docs-heading">
          <h1>{building?.address || "Архивное строение"}</h1>
          <div>
            {building?.line_name || "Линия: -"} · {building?.object_name || "Объект: -"}
          </div>
        </div>
      </header>

      {building?.source && (
        <section className="archive-source-band">
          <div><b>Наименование архива:</b> {building.source.archive_name || "Внешний диск"}</div>
          <div><b>Адрес на внешнем диске:</b> {building.source.source_path || "-"}</div>
        </section>
      )}

      <div className="archive-docs-toolbar">
        <nav className="archive-breadcrumbs" aria-label="Путь к папке">
          <button onClick={() => loadListing("")} title="Корневая папка">
            <FaHome />
            <span>{building?.documentation_folder_name || "Документация"}</span>
          </button>
          {breadcrumbs.map((crumb) => (
            <React.Fragment key={crumb.path}>
              <span>/</span>
              <button onClick={() => loadListing(crumb.path)}>{crumb.name}</button>
            </React.Fragment>
          ))}
        </nav>

        {listing.can_manage && (
          <div className="archive-docs-actions">
            <input ref={fileInputRef} type="file" multiple hidden onChange={uploadFiles} />
            <button onClick={() => fileInputRef.current?.click()} disabled={busy} title="Загрузить файлы">
              <FaUpload />
              <span>Загрузить</span>
            </button>
            <button onClick={createFolder} disabled={busy} title="Создать папку">
              <FaFolderPlus />
              <span>Создать папку</span>
            </button>
          </div>
        )}
      </div>

      {error && <div className="archive-docs-error">{error}</div>}

      <div className="archive-docs-table-wrap">
        <table className="archive-docs-table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Размер</th>
              <th>Изменено</th>
              <th aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {listing.path && (
              <tr className="archive-docs-row archive-docs-row--folder" onDoubleClick={() => loadListing(listing.parent)}>
                <td>
                  <button className="archive-entry-name" onClick={() => loadListing(listing.parent)}>
                    <FaFolder />
                    <span>..</span>
                  </button>
                </td>
                <td>-</td>
                <td>-</td>
                <td />
              </tr>
            )}

            {(listing.entries || []).map((entry) => (
              <tr key={entry.path} className={`archive-docs-row archive-docs-row--${entry.type}`}>
                <td>
                  {entry.type === "folder" ? (
                    <button className="archive-entry-name" onClick={() => loadListing(entry.path)}>
                      <FaFolder />
                      <span>{entry.name}</span>
                    </button>
                  ) : (
                    <div className="archive-entry-name archive-entry-name--file">
                      <FaFile />
                      <span>{entry.name}</span>
                    </div>
                  )}
                </td>
                <td>{entry.type === "file" ? formatSize(entry.size) : "-"}</td>
                <td>{entry.modified_at ? new Date(entry.modified_at).toLocaleString("ru-RU") : "-"}</td>
                <td>
                  <div className="archive-entry-actions">
                    {entry.type === "file" && entry.can_preview && (
                      <button onClick={() => openAuthenticatedFile(entry, "preview")} title="Предпросмотр">
                        <FaEye />
                      </button>
                    )}
                    {entry.type === "file" && (
                      <button onClick={() => openAuthenticatedFile(entry, "download")} title="Скачать">
                        <FaDownload />
                      </button>
                    )}
                    {listing.can_manage && (
                      <button onClick={() => renameEntry(entry)} disabled={busy} title="Переименовать">
                        <FaEdit />
                      </button>
                    )}
                    {listing.can_manage && (
                      <button className="is-danger" onClick={() => deleteEntry(entry)} disabled={busy} title="Удалить">
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!listing.entries?.length && !listing.path && (
          <div className="archive-docs-empty">Папка пуста</div>
        )}
      </div>
    </div>
  );
}
